import { Fragment } from "react";

import { Box, Divider, Stack, Typography } from "@mui/material";
import { useClientStatusLabel } from "@shared";

import { ClientDetailResponse } from "@/api";
import { usePortalAccountStateLabels } from "@/common/components/portal-access/hooks/usePortalAccountStateLabels";
import { useClientPortalAccountStatus } from "@/pages/clients/client/hooks/useClientPortalAccountStatus";
import { useTranslation } from "@/translations";

import { ClientStatusWidgetRow } from "./components/client-status-widget-row/ClientStatusWidgetRow";
import { CounselorPresenceIndicator } from "./components/counselor-presence-indicator/CounselorPresenceIndicator";

interface Props {
  client: ClientDetailResponse;
}

export function ClientStatusWidget({ client }: Props) {
  const { t } = useTranslation();
  const statusLabelOf = useClientStatusLabel();
  const { status, isPending } = useClientPortalAccountStatus(client.clientId);
  const stateLabels = usePortalAccountStateLabels();

  const clientAccessStatus = isPending || !status ? undefined : stateLabels[status.state];

  const rows = [
    { label: t("clients.client.status"), value: statusLabelOf(client.status) },
    {
      label: t("common.counselor"),
      value: client.assignedCounselorShortCode,
      indicator: <CounselorPresenceIndicator counselorId={client.assignedCounselorId} />,
    },
    { label: t("clients.client.idNumber"), value: client.caseNumber },
    { label: t("clients.client.clientAccess"), value: clientAccessStatus },
    { label: t("clients.client.insolvencyProceedings"), value: "Noch nicht eröffnet" },
  ];

  return (
    <Stack
      gap={1.5}
      p={2}
      pt={1.5}
      bgcolor={(theme) => theme.palette.fsw["blue-5"]}
      border={"1px"}
      borderColor={(theme) => theme.palette.fsw["blue-10"]}
    >
      <Typography variant="body1" fontWeight={"bold"}>
        {t("clients.client.clientDetails")}
      </Typography>
      <Box display="grid" gridTemplateColumns="max-content minmax(0, 1fr)" columnGap={3}>
        {rows.map((row, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider sx={{ gridColumn: "1 / -1" }} />}
            <ClientStatusWidgetRow label={row.label} value={row.value} indicator={row.indicator} />
          </Fragment>
        ))}
      </Box>
    </Stack>
  );
}
